import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Activity, Trash2 } from "lucide-react";
import { PageHeader, Stat } from "./Sugar";

function classifyBP(s, d) {
  if (s < 120 && d < 80) return { l: "Normal", c: "emerald" };
  if (s < 130 && d < 80) return { l: "Elevated", c: "amber" };
  if (s < 140 || d < 90) return { l: "Stage 1", c: "rose" };
  return { l: "Stage 2", c: "rose" };
}

export default function BP() {
  const [logs, setLogs] = useState([]);
  const [sys, setSys] = useState("");
  const [dia, setDia] = useState("");
  const [pulse, setPulse] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => { try { const { data } = await api.get("/bp"); setLogs(data); } catch {} };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/bp", { systolic: Number(sys), diastolic: Number(dia), pulse: pulse ? Number(pulse) : undefined, date });
      setSys(""); setDia(""); setPulse(""); await load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  const del = async (id) => { await api.delete(`/bp/${id}`); await load(); };

  const chartData = [...logs].reverse().map((l) => ({ t: new Date(l.measured_at || l.created_at).toLocaleDateString(), systolic: l.systolic, diastolic: l.diastolic }));
  const avg = logs.length ? `${Math.round(logs.reduce((a, l) => a + l.systolic, 0) / logs.length)}/${Math.round(logs.reduce((a, l) => a + l.diastolic, 0) / logs.length)}` : "—";

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto" data-testid="bp-page">
      <PageHeader icon={Activity} title="Blood Pressure" subtitle="Track systolic, diastolic & pulse" accent="blue" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 stagger">
        <Stat label="Latest" value={logs[0] ? `${logs[0].systolic}/${logs[0].diastolic}` : "—"} unit="mmHg" />
        <Stat label="Average" value={avg} unit="mmHg" accent="blue" />
        <Stat label="Readings" value={logs.length} unit="" accent="emerald" />
      </div>

      <form onSubmit={add} className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="bp-form">
        <div className="col-span-2 sm:col-span-1">
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</Label>
          <Input type="date" value={date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} className="mt-1 rounded-xl bg-slate-50" data-testid="bp-date" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Systolic</Label>
          <Input type="number" value={sys} onChange={(e) => setSys(e.target.value)} required className="mt-1 rounded-xl bg-slate-50" data-testid="bp-systolic" placeholder="120" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Diastolic</Label>
          <Input type="number" value={dia} onChange={(e) => setDia(e.target.value)} required className="mt-1 rounded-xl bg-slate-50" data-testid="bp-diastolic" placeholder="80" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Pulse</Label>
          <Input type="number" value={pulse} onChange={(e) => setPulse(e.target.value)} className="mt-1 rounded-xl bg-slate-50" data-testid="bp-pulse" placeholder="72" />
        </div>
        <div className="flex items-end">
          <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-blue-600 hover:bg-blue-700" data-testid="bp-submit">{loading ? "Saving…" : "Add"}</Button>
        </div>
        {error && <div className="col-span-2 sm:col-span-5 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="bp-error">{error}</div>}
      </form>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="bp-chart">
        <h3 className="font-heading font-semibold text-slate-800 mb-3">Trend</h3>
        <div className="h-64">
          {chartData.length === 0 ? (
            <div className="h-full grid place-items-center text-slate-400 text-sm">Add readings to see your trend</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="t" stroke="#94a3b8" tick={{ fontSize: 11 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 8px 24px rgba(0,0,0,0.06)" }} />
                <Line type="monotone" dataKey="systolic" stroke="#2563eb" strokeWidth={3} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="diastolic" stroke="#10b981" strokeWidth={3} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <h3 className="font-heading font-semibold text-slate-800 mb-3">Recent readings</h3>
      <div className="space-y-2" data-testid="bp-list">
        {logs.length === 0 && <div className="text-sm text-slate-400">No readings yet.</div>}
        {logs.map((l) => {
          const cat = classifyBP(l.systolic, l.diastolic);
          return (
            <div key={l.id} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900">{l.systolic}/{l.diastolic} mmHg</div>
                <div className="text-xs text-slate-500">{new Date(l.measured_at || l.created_at).toLocaleString()}{l.pulse ? ` • pulse ${l.pulse}` : ""}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full bg-${cat.c}-50 text-${cat.c}-700 border border-${cat.c}-200`}>{cat.l}</span>
                <button onClick={() => del(l.id)} data-testid={`bp-del-${l.id}`} className="text-slate-400 hover:text-rose-500 p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
